<template>
    <b-card :class="cardClass" :id="id">
        <div 
            :class="headerClass" 
            v-if="showHeader"
            v-b-toggle="headerCollapsible ? collapsibleId : null"
        >
            <slot name="header"></slot>
            <div class="st-section-title">
                <span v-if="validate" class="st-section-validation">
                    <template v-if="wasValidated">
                        <i
                            v-if="validation === 'success'"
                            :class="['fas fa-check-circle', 'text-success']"
                        ></i>
                        <i v-else class="fas fa-times-circle text-danger"></i>
                    </template>
                    <i v-else class="fas fa-times-circle text-dark-light"></i>
                </span>
                <span class="st-section-title-text">{{ header }}</span>
            </div>

            <b-button
                v-if="collapsible"
                class="collapsible"
                variant="light"
                v-b-toggle="!headerCollapsible ? `${collapsibleId}` : null"
            >
                <span v-if="!visible">
                    <i class="fas fa-chevron-down"></i>
                </span>
                <span v-else>
                    <i class="fas fa-chevron-up"></i>
                </span>
            </b-button>
        </div>

        <b-collapse v-if="collapsible" :id="collapsibleId" v-model="visible">
            <div class="st-section-body">
                <slot name="body"></slot>
            </div>
        </b-collapse>
        <div v-else class="st-section-body">
            <slot name="body"></slot>
        </div>
    </b-card>
</template>

<script>
import { BCollapse, VBToggle } from "bootstrap-vue";

export default {
    name: "StSection",
    components: {
        BCollapse,
    },
    directives: {
        "b-toggle": VBToggle,
    },
    props: {
        id: {
            type: String,
        },
        header: String,
        collapsible: {
            type: Boolean,
            default: false,
        },
        headerCollapsible: {
            type: Boolean,
            default: false,
        },
        collapsibleId: {
            type: String,
            default: "section-body",
        },
        isCollapsed: {
            type: Boolean,
            default: false,
        },
        customClass: {
            type: String,
        },
        validate: {
            type: Boolean,
            default: false,
        },
        validation: {
            type: String,
            default: "none", // 'none', 'success', 'error'
        },
        wasValidated: {
            type: Boolean,
            default: false,
        },
        marginTopSize: {
            type: String,
            default: '6',
        },
        showHeader: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            visible: !this.isCollapsed,
        };
    },
    computed: {
        headerClass() {
            let hClass = "st-section-header";
            if (!this.collapsible) {
                hClass += " not-collapsible";
            }
            if (this.headerCollapsible) {
                hClass += " header-clickable";
            }

            return hClass;
        },
        cardClass() {
            let cClass = `st-section mt-${this.marginTopSize}`;
            if (this.collapsible) {
                cClass += " st-section-slim";
            }

            if (this.customClass) {
                cClass += " " + this.customClass;
            }

            return cClass;
        },
    },
    watch:{
        visible(data) {
            this.$emit('show', {extended: data, areaId:this.collapsibleId });
        }
    },
    methods: {},
};
</script>

<style scoped>
.st-section-header.header-clickable {
    transition: background-color 0.2s ease, box-shadow 0.2s ease;
    border-radius: 0 8px 8px 0; /* Only right side rounded */
    padding: 8px 0 8px 0; /* No horizontal padding on header itself */
    margin: 0 0 0 -12px; /* Only negative left margin, keep right margin normal */
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-left: 4px solid transparent;
    border-right: 1px solid transparent;
    cursor: pointer; /* Hand cursor only when header is clickable */
}

.st-section-header.header-clickable:hover {
    background-color: rgba(0, 123, 255, 0.06);
    box-shadow: 0 0 0 1px rgba(0, 123, 255, 0.15);
    border-left-color: #007bff;
    border-right-color: rgba(0, 123, 255, 0.2);
}

.st-section-header.header-clickable .st-section-title-text {
    transition: color 0.2s ease;
    font-weight: 500;
    padding-left: 12px; /* Space from the left border */
}

.st-section-header.header-clickable:hover .st-section-title-text {
    color: #007bff;
}

/* Add specific padding for the collapsible button */
.st-section-header.header-clickable .collapsible {
    margin-right: 12px; /* Proper spacing for the dropdown button */
    padding: 8px 12px; /* Add padding inside the button itself */
    min-width: 40px; /* Ensure minimum button width */
    transition: color 0.2s ease, background-color 0.2s ease;
}

/* Button and icon hover effects */
.st-section-header.header-clickable:hover .collapsible,
.st-section-header.header-clickable .collapsible:hover,
.st-section-header.header-clickable .collapsible:focus {
    color: #007bff !important;
    background-color: transparent !important;
    border-color: transparent !important;
    border-radius: 0 !important; /* Remove border radius on hover */
    box-shadow: none !important; /* Remove any button focus shadow */
}

.st-section-header.header-clickable:hover .collapsible i,
.st-section-header.header-clickable .collapsible:hover i,
.st-section-header.header-clickable .collapsible i:hover,
.st-section-header.header-clickable .collapsible:focus i {
    color: #007bff !important;
}
</style>
